'use client';

import { KeyboardEvent, useEffect, useRef, useState } from 'react';

import './css/TabList.scss';
import { TabData, TabDataItem } from './Tabs';

export interface TabListProps {
  tabData?: TabData;
  labelledBy?: string;
  previousTabAriaLabel?: string;
  nextTabAriaLabel?: string;
}

const scrollTimeoutMs = 500;

export const TabList = ({
  tabData = [],
  labelledBy,
  previousTabAriaLabel = 'Previous Tab',
  nextTabAriaLabel = 'Next Tab',
}: TabListProps) => {
  // @ts-expect-error - review this later for an appropriate type
  const scrollTimeout = useRef<ReturnType<typeof setTimeout>>();

  // readonly refs
  const container = useRef<HTMLDivElement>(null);
  const previousButton = useRef<HTMLButtonElement>(null);
  const nextButton = useRef<HTMLButtonElement>(null);
  const tabsWrapper = useRef<HTMLDivElement>(null);

  // mutable refs (see https://www.designcise.com/web/tutorial/how-to-fix-useref-react-hook-cannot-assign-to-read-only-property-typescript-error)
  const tabs = useRef<Array<HTMLSpanElement | null>>([]);
  const firstTab = useRef<HTMLSpanElement | null>(null);
  const lastTab = useRef<HTMLSpanElement | null>(null);
  const previousHiddenTab = useRef<HTMLSpanElement | null>(null);
  const nextHiddenTab = useRef<HTMLSpanElement | null>(null);

  const [isPreviousButtonEnabled, setIsPreviousButtonEnabled] = useState(false);
  const [isNextButtonEnabled, setIsNextButtonEnabled] = useState(false);
  const [hideScrollButtons, setHideScrollButtons] = useState(false);

  const updateButtons = () => {
    setIsPreviousButtonEnabled(!!previousHiddenTab.current);
    setIsNextButtonEnabled(!!nextHiddenTab.current);
    setHideScrollButtons(!previousHiddenTab.current && !nextHiddenTab.current);
  };

  const focusButton = (button: HTMLButtonElement) => {
    tabs.current.forEach((tab) =>
      tab?.querySelector('button')?.setAttribute('tabindex', '-1')
    );
    button.removeAttribute('tabindex');
    button.focus();
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      const isVisible = entry.isIntersecting;
      const item = entry.target as HTMLSpanElement;
      item.setAttribute('data-hidden', `${!isVisible}`);
    });

    const firstVisibleItem = tabsWrapper.current?.querySelector(
      '[data-hidden="false"]'
    );

    if (firstVisibleItem) {
      previousHiddenTab.current = null;
      let previousSibling = firstVisibleItem.previousElementSibling;
      while (previousSibling) {
        if (previousSibling.getAttribute('data-hidden') !== 'false') {
          previousHiddenTab.current = previousSibling as HTMLElement;
          break;
        }
        previousSibling = previousSibling.previousElementSibling;
      }

      nextHiddenTab.current = null;
      let nextSibling = firstVisibleItem.nextElementSibling;

      while (nextSibling) {
        if (nextSibling.getAttribute('data-hidden') !== 'false') {
          nextHiddenTab.current = nextSibling as HTMLElement;
          break;
        }
        nextSibling = nextSibling.nextElementSibling;
      }
    }

    updateButtons();
  };

  const scrollToFirstTab = () => {
    const buttonToFocus = firstTab.current?.querySelector(
      'button'
    ) as HTMLButtonElement;
    if (firstTab.current?.getAttribute('data-hidden') === 'true') {
      firstTab.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
      const buttonToFocus = firstTab.current.querySelector(
        'button'
      ) as HTMLButtonElement;
      updateButtons();
      scrollTimeout.current = setTimeout(
        () => focusButton(buttonToFocus),
        scrollTimeoutMs
      );
    } else {
      focusButton(buttonToFocus);
    }
  };

  const scrollToLastTab = () => {
    const buttonToFocus = lastTab.current?.querySelector(
      'button'
    ) as HTMLButtonElement;
    if (lastTab.current?.getAttribute('data-hidden') === 'true') {
      lastTab.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'end',
      });
      updateButtons();
      scrollTimeout.current = setTimeout(
        () => focusButton(buttonToFocus),
        scrollTimeoutMs
      );
    } else {
      focusButton(buttonToFocus);
    }
  };

  const scrollToNextHiddenTab = () => {
    if (nextHiddenTab.current) {
      nextHiddenTab.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
      const buttonToFocus = nextHiddenTab.current.querySelector(
        'button'
      ) as HTMLButtonElement;
      updateButtons();
      scrollTimeout.current = setTimeout(
        () => focusButton(buttonToFocus),
        scrollTimeoutMs
      );
    }
  };

  const scrollToPreviousHiddenTab = () => {
    if (previousHiddenTab.current) {
      previousHiddenTab.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'end',
      });
      const buttonToFocus = previousHiddenTab.current.querySelector(
        'button'
      ) as HTMLButtonElement;
      updateButtons();
      scrollTimeout.current = setTimeout(
        () => focusButton(buttonToFocus),
        scrollTimeoutMs
      );
    }
  };

  const moveFocusToNextTab = (currentTarget: HTMLButtonElement) => {
    if (currentTarget === lastTab.current?.querySelector('button')) {
      scrollToFirstTab();
    } else {
      const parentNode = currentTarget.parentNode as HTMLSpanElement;
      const index = tabs.current.indexOf(parentNode);
      const nextTab = tabs.current[index + 1];
      if (
        parentNode.getAttribute('data-hidden') === 'false' &&
        nextTab &&
        nextTab.getAttribute('data-hidden') !== 'false'
      ) {
        scrollToNextHiddenTab();
      } else {
        focusButton(
          tabs.current[index + 1]?.querySelector('button') as HTMLButtonElement
        );
      }
    }
  };

  const moveFocusToPreviousTab = (currentTarget: HTMLElement) => {
    if (currentTarget === firstTab.current?.querySelector('button')) {
      scrollToLastTab();
    } else {
      const parentNode = currentTarget.parentNode as HTMLSpanElement;
      const index = tabs.current.indexOf(parentNode);
      const previousTab = tabs.current[index - 1];
      if (
        parentNode.getAttribute('data-hidden') === 'false' &&
        previousTab &&
        previousTab.getAttribute('data-hidden') !== 'false'
      ) {
        scrollToPreviousHiddenTab();
      } else {
        focusButton(
          tabs.current[index - 1]?.querySelector('button') as HTMLButtonElement
        );
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    const currentTab = (e.target as HTMLElement).closest(
      '[role="tab"]'
    ) as HTMLButtonElement;
    if (currentTab) {
      const stopEvent = () => {
        clearTimeout(scrollTimeout.current);
        e.stopPropagation();
        e.preventDefault();
      };

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          stopEvent();
          moveFocusToNextTab(currentTab);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          stopEvent();
          moveFocusToPreviousTab(currentTab);
          break;
        case 'Home':
          stopEvent();
          scrollToFirstTab();
          break;
        case 'End':
          stopEvent();
          scrollToLastTab();
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    // trim stale refs when tabs are removed
    tabs.current = tabs.current.slice(0, tabData.length);

    // ensure there is always a focusable tab button after changes
    const buttons = tabsWrapper.current?.querySelectorAll('button');
    if (buttons && buttons.length > 0) {
      const hasFocusable = Array.from(buttons).some(
        (btn) => btn.getAttribute('tabindex') !== '-1'
      );
      if (!hasFocusable) {
        buttons[0].removeAttribute('tabindex');
      }
    }

    [firstTab.current] = tabs.current;
    lastTab.current = tabs.current[tabs.current.length - 1];

    const intersectionObserver = new IntersectionObserver(handleIntersection, {
      root: container.current,
      threshold: 0.99,
    });

    tabs?.current.forEach((span) => {
      if (span) {
        intersectionObserver.observe(span as HTMLSpanElement);
      }
    });

    // initialize data-hidden on new items so the sibling walk in
    // handleIntersection can correctly find hidden neighbors
    if (container.current) {
      const containerRect = container.current.getBoundingClientRect();
      tabs.current.forEach((span) => {
        if (span && !span.hasAttribute('data-hidden')) {
          const spanRect = span.getBoundingClientRect();
          const isVisible =
            spanRect.right <= containerRect.right + 1 &&
            spanRect.left >= containerRect.left - 1;
          span.setAttribute('data-hidden', `${!isVisible}`);
        }
      });
    }
    // run the sibling walk to set previousHiddenTab/nextHiddenTab,
    // then update scroll button visibility
    handleIntersection([]);

    return () => {
      intersectionObserver.disconnect();
      clearTimeout(scrollTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabData.length]);

  return (
    <div
      className="ps-tablist"
      onKeyDown={handleKeyDown}
    >
      <button
        className="ps-tablist-previous"
        onClick={scrollToPreviousHiddenTab}
        disabled={!isPreviousButtonEnabled}
        aria-label={previousTabAriaLabel}
        ref={previousButton}
        tabIndex={isPreviousButtonEnabled ? 0 : -1}
        hidden={hideScrollButtons}
      >
        <svg
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="m7.79 5.18-2.31 2.31h6.57s0 1.03 0 1.03h-6.57l2.3 2.3-.73.73-2.82-2.82c-.2-.2-.3-.46-.3-.73 0-.26.1-.53.3-.73l2.83-2.83.73.73z"
            fill="#262626"
          />
        </svg>
      </button>
      <div
        className="ps-tablist-container"
        ref={container}
      >
        <div
          role="tablist"
          className="ps-tablist-tabs-wrapper"
          aria-labelledby={labelledBy}
          ref={tabsWrapper}
        >
          {tabData?.map((data: TabDataItem, i) => (
            <span
              // @ts-expect-error - review this later for an appropriate type
              ref={(el) => (tabs.current[i] = el)}
              key={data.button.props.id}
            >
              {data.button}
            </span>
          ))}
        </div>
      </div>
      <button
        className="ps-tablist-next"
        onClick={scrollToNextHiddenTab}
        disabled={!isNextButtonEnabled}
        aria-label={nextTabAriaLabel}
        ref={nextButton}
        tabIndex={isNextButtonEnabled ? 0 : -1}
        hidden={hideScrollButtons}
      >
        <svg
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="m8.21 10.82 2.31-2.31h-6.58s0-1.03 0-1.03h6.58l-2.3-2.3.73-.73 2.82 2.82c.2.2.3.46.3.73 0 .26-.1.53-.3.73l-2.83 2.83-.73-.73z"
            fill="#262626"
          />
        </svg>
      </button>
    </div>
  );
};
